import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { ChevronLeft, ChevronRight, ChevronDown, Loader2 } from "lucide-react";
import { useRef, useEffect, useState, useMemo, forwardRef, useImperativeHandle } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { useProcessingStatus } from "@/hooks/use-processing-status";
import { ProcessingSkeleton } from "@/components/processing-skeleton";
import { PageAdminControls } from "@/components/page-admin-controls";
import type { DocumentPage, DocumentVersion } from "@shared/schema";

export interface FilmstripSidebarHandle {
  scrollToEnd: () => Promise<void>;
  scrollToStart: () => Promise<void>;
  flashPages: (pageIds: string[]) => void;
}

interface PageItemProps {
  page: DocumentPage;
  currentPage: number;
  currentPageId: string | null;
  documentColor: string;
  isScrollDragging: boolean;
  onPageSelect: (pageId: string, pageNumber: number) => void;
  onToggleVisibility: (pageId: string, currentHidden: boolean) => void;
  onInsertPlaceholder?: (afterPosition: number, label: string) => void;
  allPages: DocumentPage[];
  shouldFlash: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

function PageItem({
  page,
  currentPage,
  currentPageId,
  documentColor,
  isScrollDragging,
  onPageSelect,
  onToggleVisibility,
  onInsertPlaceholder,
  allPages,
  shouldFlash,
  onDragStart,
  onDragEnd
}: PageItemProps) {
  const controls = useDragControls();
  
  // Use ID for highlighting to handle reordering correctly
  const isCurrentPage = currentPageId === page.id;
  
  // Calculate position as index in VISIBLE pages array (for display numbering 1,2,3,4...)
  const visiblePages = allPages.filter(p => !p.hidden);
  const pagePosition = visiblePages.findIndex(p => p.id === page.id) + 1;

  // Flash animation controlled by parent
  const [isFresh, setIsFresh] = useState(false);

  useEffect(() => {
    if (shouldFlash) {
      setIsFresh(true);
      const timer = setTimeout(() => {
        setIsFresh(false);
      }, 1600);
      return () => clearTimeout(timer);
    }
  }, [shouldFlash]);

  return (
    <Reorder.Item
      key={page.id}
      value={page}
      as="div"
      className="relative flex-shrink-0"
      dragControls={controls}
      dragListener={false}
      data-reorderable="true"
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      whileDrag={{
        scale: 1.05,
        zIndex: 10,
        boxShadow: "0 10px 30px rgba(0,0,0,0.2)"
      }}
      transition={{
        layout: { type: "spring", stiffness: 300, damping: 30 }
      }}
      style={{ pointerEvents: 'auto' }}
    >
      <button
        className={`flex-shrink-0 p-1 rounded-lg transition-all duration-200 touch-manipulation group relative ${
          isCurrentPage 
            ? "bg-background shadow-md" 
            : "hover:bg-muted/50 active:bg-muted"
        }`}
        style={{
          pointerEvents: 'auto',
          ...(isCurrentPage && {
            background: `linear-gradient(135deg, ${documentColor}08, ${documentColor}03)`
          }),
          ...(isFresh ? {
            ['--ring-color' as string]: documentColor,
            animation: `pulse-ring 0.8s ease-in-out 2`,
          } : isCurrentPage ? {
            animation: 'none',
            boxShadow: `0 0 0 2px ${documentColor}`,
          } : {
            animation: 'none',
          })
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (!isScrollDragging) {
            onPageSelect(page.id, pagePosition);
          }
        }}
      >
        <div 
          className="bg-muted rounded-md overflow-hidden shadow-sm flex items-center justify-center"
          style={{ 
            aspectRatio: `${page.width}/${page.height}`,
            width: "72px",
            maxWidth: "90px",
            minWidth: "50px",
            ...(page.isPlaceholder ? {
              backgroundColor: documentColor + '20',
              border: `2px dashed ${documentColor}60`
            } : {})
          }}
        >
          {page.isPlaceholder ? (
            <div className="flex flex-col items-center justify-center gap-0.5 p-1">
              <div 
                className="text-[7px] sm:text-[8px] font-bold text-center leading-tight line-clamp-2 px-0.5"
                style={{ color: documentColor }}
                title={page.label || "New Slide"}
              >
                {page.label || "NEW SLIDE"}
              </div>
            </div>
          ) : (
            <img
              src={page.webpUrl || ''}
              alt={`Page ${pagePosition}`}
              className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
              loading="lazy"
              draggable={false}
            />
          )}
        </div>
        
        <div 
          className={`text-xs mt-1 text-center font-medium transition-colors duration-200 ${
            isCurrentPage 
              ? "font-bold" 
              : "text-foreground/70"
          }`}
          style={{
            color: isCurrentPage ? documentColor : undefined
          }}
        >
          {pagePosition}
        </div>

        {((page as any).hasOpenIndicators) && (
          <div 
            className={`absolute -top-1 -right-1 w-3 h-3 rounded-full shadow-sm ring-2 ring-background ${
              isCurrentPage ? 'opacity-100' : 'opacity-90'
            }`}
            style={{ backgroundColor: documentColor }}
          />
        )}
      </button>

      <PageAdminControls
        page={page}
        onToggleVisibility={onToggleVisibility}
        onInsertPlaceholder={onInsertPlaceholder ? (displayOrderAfter, label) => {
          // Convert from page's displayOrder to insertion position (insert after this page's displayOrder)
          onInsertPlaceholder(displayOrderAfter + 1, label);
        } : undefined}
        dragControls={controls}
        pagePosition={pagePosition}
        pageDisplayOrder={page.displayOrder}
        documentColor={documentColor}
      />
    </Reorder.Item>
  );
}

interface FilmstripSidebarProps {
  pages: DocumentPage[];
  currentPage: number;
  currentPageId: string | null;
  onPageSelect: (pageId: string, pageNumber: number) => void;
  isLoading: boolean;
  selectedVersion: DocumentVersion | null;
  documentColor?: string;
  onReorderPages?: (updates: { pageId: string; displayOrder: number }[]) => void;
  onTogglePageVisibility?: (pageId: string, hidden: boolean) => void;
  onInsertPlaceholder?: (afterPosition: number, label: string) => void;
  isReordering?: boolean;
}

export const FilmstripSidebar = forwardRef<FilmstripSidebarHandle, FilmstripSidebarProps>(({ 
  pages, 
  currentPage,
  currentPageId,
  onPageSelect, 
  isLoading,
  selectedVersion,
  documentColor = "#ef4444",
  onReorderPages,
  onTogglePageVisibility,
  onInsertPlaceholder,
  isReordering = false
}, ref) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [isScrollDragging, setIsScrollDragging] = useState(false);
  const [isReorderDragging, setIsReorderDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [pointerDownStart, setPointerDownStart] = useState<{ x: number; y: number } | null>(null);
  
  // Hidden pages state
  const [showHiddenPages, setShowHiddenPages] = useState(false);
  
  const [pagesToFlash, setPagesToFlash] = useState<Set<string>>(new Set());

  // Expose imperative methods
  useImperativeHandle(ref, () => ({
    scrollToEnd: () => {
      return new Promise<void>((resolve) => {
        const container = scrollContainerRef.current;
        if (!container) {
          resolve();
          return;
        }

        const targetScrollLeft = container.scrollWidth - container.clientWidth;
        
        // If already at the end, resolve immediately
        if (Math.abs(container.scrollLeft - targetScrollLeft) < 0.5) {
          resolve();
          return;
        }

        // Start scroll
        container.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth'
        });

        // Watch for scroll completion - wait until within 0.5px of target
        let rafId: number;

        const checkScroll = () => {
          if (!container) {
            resolve();
            return;
          }

          const currentScrollLeft = container.scrollLeft;
          
          // Check if we're within 0.5px of target position
          if (Math.abs(currentScrollLeft - targetScrollLeft) < 0.5) {
            // Force final snap to exact position
            container.scrollLeft = targetScrollLeft;
            resolve();
            return;
          }

          rafId = requestAnimationFrame(checkScroll);
        };

        rafId = requestAnimationFrame(checkScroll);

        // Fallback timeout after 2 seconds
        setTimeout(() => {
          cancelAnimationFrame(rafId);
          // Force final position even on timeout
          container.scrollLeft = targetScrollLeft;
          resolve();
        }, 2000);
      });
    },

    scrollToStart: () => {
      return new Promise<void>((resolve) => {
        const container = scrollContainerRef.current;
        if (!container) {
          resolve();
          return;
        }

        const targetScrollLeft = 0;
        
        // If already at the start, resolve immediately
        if (Math.abs(container.scrollLeft - targetScrollLeft) < 0.5) {
          resolve();
          return;
        }

        // Start scroll
        container.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth'
        });

        // Watch for scroll completion - wait until within 0.5px of target
        let rafId: number;

        const checkScroll = () => {
          if (!container) {
            resolve();
            return;
          }

          const currentScrollLeft = container.scrollLeft;
          
          // Check if we're within 0.5px of target position
          if (Math.abs(currentScrollLeft - targetScrollLeft) < 0.5) {
            // Force final snap to exact position
            container.scrollLeft = targetScrollLeft;
            resolve();
            return;
          }

          rafId = requestAnimationFrame(checkScroll);
        };

        rafId = requestAnimationFrame(checkScroll);

        // Fallback timeout after 2 seconds
        setTimeout(() => {
          cancelAnimationFrame(rafId);
          // Force final position even on timeout
          container.scrollLeft = targetScrollLeft;
          resolve();
        }, 2000);
      });
    },

    flashPages: (pageIds: string[]) => {
      setPagesToFlash(new Set(pageIds));
      // Reset after flash duration
      setTimeout(() => {
        setPagesToFlash(new Set());
      }, 2000);
    }
  }), []);
  
  // Auto-scroll to reveal hidden pages when section opens
  useEffect(() => {
    if (showHiddenPages && scrollContainerRef.current) {
      // Scroll all the way to the right to show hidden pages
      const container = scrollContainerRef.current;
      setTimeout(() => {
        container.scrollTo({
          left: container.scrollWidth,
          behavior: 'smooth'
        });
      }, 100); // Small delay to let DOM update
    }
  }, [showHiddenPages]);

  
  // Local state for visual reordering during drag (instant feedback)
  const [localVisiblePages, setLocalVisiblePages] = useState<DocumentPage[] | null>(null);
  
  // Get visible pages for Reorder (memoized to prevent unnecessary re-renders)
  // Use local state during drag for instant visual feedback, otherwise use server state
  const visiblePages = useMemo(() => {
    if (localVisiblePages) return localVisiblePages;
    return pages.filter(p => !p.hidden);
  }, [pages, localVisiblePages]);

  // Unified pointer event handling for scroll dragging
  const handlePointerDown = (e: React.PointerEvent) => {
    // Don't start scroll drag if a reorder drag is active
    if (isReorderDragging) {
      return;
    }
    
    const target = e.target as HTMLElement;
    
    // Only block drag handle - allow scrolling from thumbnails
    if (target.closest('[data-drag-handle="true"]')) {
      return; // Let the drag handle control take over
    }
    
    // Record pointer down position but don't start dragging yet
    // We'll wait to see if they move beyond threshold
    // Don't capture pointer yet - only after threshold is crossed
    setPointerDownStart({ x: e.clientX, y: e.clientY });
    setStartX(e.clientX);
    setScrollLeft(scrollContainerRef.current?.scrollLeft || 0);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!scrollContainerRef.current) return;
    
    // If pointer is down but we haven't started dragging yet, check threshold
    if (pointerDownStart && !isScrollDragging) {
      const deltaX = Math.abs(e.clientX - pointerDownStart.x);
      const deltaY = Math.abs(e.clientY - pointerDownStart.y);
      const totalMovement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      // Start dragging if moved beyond 5px threshold
      if (totalMovement > 5) {
        setIsScrollDragging(true);
        setPointerDownStart(null);
        // NOW capture the pointer since we're confirmed dragging
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        // Immediately apply scroll for this first move event
        e.preventDefault();
        const x = e.clientX;
        const walk = (x - startX) * 2;
        scrollContainerRef.current.scrollLeft = scrollLeft - walk;
      }
      return;
    }
    
    if (!isScrollDragging) return;
    e.preventDefault();
    const x = e.clientX;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (isScrollDragging && scrollContainerRef.current) {
      try {
        scrollContainerRef.current.releasePointerCapture(e.pointerId);
      } catch (error) {
        // Pointer may have been released already
      }
    }
    setIsScrollDragging(false);
    setPointerDownStart(null);
  };

  const handlePointerCancel = (e: React.PointerEvent) => {
    if (isScrollDragging && scrollContainerRef.current) {
      try {
        scrollContainerRef.current.releasePointerCapture(e.pointerId);
      } catch (error) {
        // Pointer may have been released already
      }
    }
    setIsScrollDragging(false);
    setPointerDownStart(null);
  };

  // Framer Motion reorder handler - VISUAL ONLY, instant feedback with renumbered pages
  const handleReorder = (newVisibleOrder: DocumentPage[]) => {
    console.log('🎨 FILMSTRIP handleReorder called, new order:', newVisibleOrder.map(p => p.id));
    // Renumber pages based on new position for instant visual feedback
    const renumberedPages = newVisibleOrder.map((page, index) => ({
      ...page,
      pageNumber: index + 1 // New page number matches visual position
    }));
    setLocalVisiblePages(renumberedPages);
    console.log('✨ FILMSTRIP localVisiblePages updated');
  };

  // Persist to server when drag ends
  const handleDragEnd = () => {
    console.log('🏁 FILMSTRIP handleDragEnd called');
    console.log('📊 FILMSTRIP state - isReorderDragging:', isReorderDragging);
    console.log('📦 FILMSTRIP state - localVisiblePages:', localVisiblePages);
    console.log('🔧 FILMSTRIP state - onReorderPages exists:', !!onReorderPages);
    
    setIsReorderDragging(false);
    
    // If we have local changes, persist them to server
    // Keep local state until server responds - don't clear here!
    if (localVisiblePages && onReorderPages) {
      console.log('✅ FILMSTRIP - conditions met, building updates...');
      const updates: { pageId: string; displayOrder: number }[] = [];
      
      // Strategy: Reconstruct full array by interleaving reordered visible pages with hidden pages at their original slots
      // 1. Get hidden pages with their current positions
      const hiddenPagesWithPos = pages
        .map((p, idx) => ({ page: p, originalIndex: idx }))
        .filter(({ page }) => page.hidden);
      
      // 2. Build new full array by inserting visible and hidden pages
      const newFullArray: DocumentPage[] = [];
      let visibleIdx = 0;
      let hiddenIdx = 0;
      
      // Interleave visible (from localVisiblePages) and hidden (at original positions)
      for (let i = 0; i < pages.length; i++) {
        // Check if there's a hidden page that should be at this position
        if (hiddenIdx < hiddenPagesWithPos.length && hiddenPagesWithPos[hiddenIdx].originalIndex === i) {
          newFullArray.push(hiddenPagesWithPos[hiddenIdx].page);
          hiddenIdx++;
        } else if (visibleIdx < localVisiblePages.length) {
          // Insert next visible page from reordered list
          newFullArray.push(localVisiblePages[visibleIdx]);
          visibleIdx++;
        }
      }
      
      // 3. Assign displayOrder based on new array position
      newFullArray.forEach((page, index) => {
        updates.push({ pageId: page.id, displayOrder: index });
      });
      
      console.log('📤 FILMSTRIP calling onReorderPages with updates:', updates);
      onReorderPages(updates);
      console.log('✨ FILMSTRIP onReorderPages called successfully');
      // Don't clear localVisiblePages here - let effect handle it when server responds
    } else {
      console.log('❌ FILMSTRIP - conditions NOT met, skipping persist');
      if (!localVisiblePages) console.log('  ❌ localVisiblePages is null/undefined');
      if (!onReorderPages) console.log('  ❌ onReorderPages callback is null/undefined');
    }
  };
  
  // Clear local state when server order matches our optimistic order
  useEffect(() => {
    if (!localVisiblePages) return;
    
    // Get current server visible pages
    const serverVisiblePages = pages.filter(p => !p.hidden);
    
    // Check if server order matches local order by comparing IDs in sequence
    const serverIds = serverVisiblePages.map(p => p.id).join(',');
    const localIds = localVisiblePages.map(p => p.id).join(',');
    
    if (serverIds === localIds) {
      // Server has caught up to our optimistic state - clear it
      setLocalVisiblePages(null);
    }
  }, [pages, localVisiblePages]);

  const handleTogglePageVisibility = (pageId: string, currentHidden: boolean) => {
    console.log('🎬 FILMSTRIP handleTogglePageVisibility called');
    console.log('   pageId:', pageId);
    console.log('   currentHidden:', currentHidden);
    console.log('   will call onTogglePageVisibility with hidden:', !currentHidden);
    console.log('   callback exists:', !!onTogglePageVisibility);
    if (!onTogglePageVisibility) {
      console.log('   ❌ No onTogglePageVisibility callback!');
      return;
    }
    onTogglePageVisibility(pageId, !currentHidden);
    console.log('   ✅ Called onTogglePageVisibility');
  };

  // Check scroll position to show/hide arrows
  const checkScrollPosition = () => {
    if (!scrollContainerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
  };


  // Initial scroll position check
  useEffect(() => {
    checkScrollPosition();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      return () => container.removeEventListener('scroll', checkScrollPosition);
    }
  }, [pages]);

  const scrollToDirection = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return;
    const scrollAmount = 200;
    scrollContainerRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  // Processing status detection
  const processingStatus = useProcessingStatus(
    !isLoading && pages.length === 0 && selectedVersion ? selectedVersion.id : null
  );

  // Hide filmstrip completely if no pages yet - don't show loading skeletons
  if (pages.length === 0) {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-lg relative overflow-hidden">
      <div className="pt-1 relative">
        
        {/* Left scroll arrow */}
        {showLeftArrow && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-1 top-1/2 -translate-y-1/2 z-10 h-8 w-8 bg-background/80 backdrop-blur-sm shadow-md hover:bg-background/90 touch-optimized"
            style={{ borderColor: documentColor }}
            onClick={() => scrollToDirection('left')}
          >
            <ChevronLeft className="h-4 w-4" style={{ color: documentColor }} />
          </Button>
        )}

        {/* Right scroll arrow */}
        {showRightArrow && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 z-10 h-8 w-8 bg-background/80 backdrop-blur-sm shadow-md hover:bg-background/90 touch-optimized"
            style={{ borderColor: documentColor }}
            onClick={() => scrollToDirection('right')}
          >
            <ChevronRight className="h-4 w-4" style={{ color: documentColor }} />
          </Button>
        )}

        {/* Scrollable filmstrip with inline hidden pages */}
        <div
          ref={scrollContainerRef}
          className="flex gap-2 overflow-x-auto pb-2 px-1 pt-1 custom-scrollbar select-none"
          style={{ 
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
            cursor: isScrollDragging ? 'grabbing' : 'grab'
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onPointerLeave={handlePointerUp}
        >
          {/* Reorder group for visible pages only */}
          <Reorder.Group
            as="div"
            axis="x"
            values={visiblePages}
            onReorder={handleReorder}
            className="flex gap-2"
          >
            {visiblePages.map((page) => (
              <PageItem
                key={page.id}
                page={page}
                currentPage={currentPage}
                currentPageId={currentPageId}
                documentColor={documentColor}
                isScrollDragging={isScrollDragging}
                onPageSelect={onPageSelect}
                onToggleVisibility={handleTogglePageVisibility}
                onInsertPlaceholder={onInsertPlaceholder}
                allPages={pages}
                shouldFlash={pagesToFlash.has(page.id)}
                onDragStart={() => {
                  console.log('🚀 FILMSTRIP onDragStart called');
                  setIsReorderDragging(true);
                  setIsScrollDragging(false);
                }}
                onDragEnd={handleDragEnd}
              />
            ))}
          </Reorder.Group>

          {/* Hidden pages section - INLINE with filmstrip */}
          {pages.filter(p => p.hidden).length > 0 && (
            <>
              {/* Vertical divider */}
              <Separator orientation="vertical" className="h-24 flex-shrink-0 ml-2" />
              
              {/* Toggle trigger for hidden pages */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowHiddenPages(!showHiddenPages);
                }}
                onPointerDown={(e) => e.stopPropagation()}
                className="flex-shrink-0 flex items-center py-2 px-2 text-xs text-muted-foreground hover:text-foreground transition-all duration-200"
                data-testid="trigger-hidden-pages"
              >
                <ChevronLeft 
                  className={`h-4 w-4 transition-transform duration-200 ${showHiddenPages ? 'scale-x-[-1]' : ''}`}
                />
              </button>
              
              {/* Hidden pages inline in filmstrip when expanded */}
              {showHiddenPages && pages.filter(p => p.hidden).map((page) => {
                console.log('🎭 Rendering hidden page:', page.id, 'hidden:', page.hidden);
                // Hidden pages show their TRUE array position (where they'll return when unhidden)
                const trueArrayPosition = pages.findIndex(p => p.id === page.id) + 1;
                // But we need to pass the visible page equivalent for navigation
                const visiblePages = pages.filter(p => !p.hidden);
                const visiblePageNumber = visiblePages.length + 1; // Next available visible slot
                return (
                  <div key={page.id} className="relative flex-shrink-0">
                    <button
                      className="flex-shrink-0 p-1 rounded-lg transition-all duration-200 hover:bg-muted/50 opacity-60 cursor-pointer"
                      onClick={() => onPageSelect(page.id, visiblePageNumber)}
                      data-testid={`button-hidden-page-${page.id}`}
                    >
                      <div 
                        className="bg-muted rounded-md overflow-hidden shadow-sm"
                        style={{ 
                          aspectRatio: `${page.width}/${page.height}`,
                          width: "72px",
                          maxWidth: "90px",
                          minWidth: "50px"
                        }}
                      >
                        <img
                          src={page.webpUrl || ''}
                          alt={`Hidden Page ${trueArrayPosition}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          draggable={false}
                        />
                      </div>
                      <div className="text-xs mt-1 text-center text-muted-foreground">
                        {trueArrayPosition}
                      </div>
                    </button>

                    {/* Admin controls for hidden pages */}
                    <PageAdminControls
                      page={page}
                      onToggleVisibility={handleTogglePageVisibility}
                      pagePosition={trueArrayPosition}
                    />
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
});
